// react
import { useCallback, useContext, useEffect, useState } from 'react';

// azure
import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';

// router
import { useNavigate, useSearchParams } from 'react-router-dom';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// hooks
import { useAuth } from '../../../hooks/useAuth';

// context
import { ContextUser } from '../../../App.context';

// mui
import { Alert, Grid, Typography, Button, Link, AlertTitle } from '@mui/material';

// layouts
import { Gateway } from '../../layouts/Gateway/Gateway';

// img
import { LogoWorldBankGroup } from '../../img/LogoWorldBankGroup';

// ui
import { Empty } from '../../ui/Empty/Empty';
import { Feedback, LoadingStatus } from '../../../@types';
import { LoadingButton } from '@mui/lab';

// fields
import { SelectLanguage } from '../../fields/SelectLanguage/SelectLanguage';

export const PageLogin = () => {
	// hooks
	const { login, logout } = useAuth();
	const isAuthenticated = useIsAuthenticated();
	const { inProgress, instance } = useMsal();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { user } = useContext(ContextUser);

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [userEmail, setUserEmail] = useState<string | null>(null);
	const [status, setStatus] = useState<LoadingStatus>('LOADING');

	// params
	const paramError = searchParams.get('error');
	const paramCode = searchParams.get('code');
	const paramIsLoginImmediate = searchParams.get('isLoginImmediate');

	// vars
	const isLoading = status === 'LOADING';

	useEffect(() => {
		// Detect if auth flow is in process - this helps prevent the login form flash
		if ((paramCode || inProgress !== InteractionStatus.None) && !paramError) {
			setStatus('LOADING');
		} else if (!isAuthenticated && inProgress === InteractionStatus.None) {
			// Set status to DEFAULT when there's no authentication in progress and user is not authenticated
			setStatus('DEFAULT');
		}

		// Get user email from MSAL accounts
		const accounts = instance.getAllAccounts();
		if (accounts.length > 0) {
			const account = accounts[0];
			// Set user email from account - check multiple possible locations
			const email =
				account.username ||
				(account.idTokenClaims && 'email' in account.idTokenClaims
					? (account.idTokenClaims.email as string)
					: undefined) ||
				(account.idTokenClaims && 'preferred_username' in account.idTokenClaims
					? (account.idTokenClaims.preferred_username as string)
					: undefined) ||
				account.name ||
				null;

			setUserEmail(email || null);
		} else {
			setUserEmail(null);
		}
	}, [isAuthenticated, inProgress, user, paramError, paramCode, instance]);

	const handleLogin = useCallback(async () => {
		setStatus('LOADING');
		try {
			await login();
		} catch (err) {
			setStatus('DEFAULT');
			const feedbackNew: Feedback = {
				alert: {
					children: t('error.logoutFailed'),
				},
			};
			setFeedback(feedbackNew);
		}
	}, [login, t]);

	const handleLogout = async () => {
		setStatus('LOADING');
		await logout();
		setStatus('DEFAULT');
	};

	const renderContent = () => {
		// If there's an auth error, provide clear recovery options
		if (paramError) {
			return (
				<>
					{feedback && (
						<Grid item xs={12}>
							<Alert severity="error" {...feedback.alert}>
								{feedback.title && <AlertTitle {...feedback.title} />}
								{feedback.alert.children}
							</Alert>
						</Grid>
					)}

					<Grid item xs={12}>
						<Typography variant="body2" color="textSecondary" gutterBottom>
							{t('login.errorRecovery')}
						</Typography>
					</Grid>

					<Grid item xs={12}>
						<LoadingButton
							fullWidth
							loading={status === 'LOADING'}
							onClick={handleLogout}
							variant="contained"
							sx={{ mb: 2 }}>
							{userEmail ? `${t('switchAccount')} (${userEmail})` : t('switchAccount')}
						</LoadingButton>
					</Grid>
				</>
			);
		}

		// Show loading indicator when explicitly set or when authentication is in process
		if (isLoading || inProgress !== InteractionStatus.None) {
			return (
				<Grid item xs={12}>
					<Empty
						isLoading={true}
						title={{ children: isAuthenticated ? t('redirecting') : t('loggingIn') }}
					/>
				</Grid>
			);
		}

		// Default login view
		return (
			<>
				<Grid item xs={12}>
					<Typography variant="caption">
						<Trans
							components={{
								anchor: <Link href="https://worldbank.org/fundschain" target="_blank" />,
							}}
							i18nKey="login.intro"
						/>
					</Typography>
				</Grid>

				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}

				<Grid item xs={12}>
					<Button
						color="primary"
						disabled={isLoading}
						fullWidth
						onClick={handleLogin}
						variant="contained">
						{t('signIn')}
					</Button>
				</Grid>

				<Grid item xs={12}>
					<Typography color="brand.grey.500" variant="caption">
						<Trans
							components={{
								anchor: <Link href="https://worldbank.org/en/about/legal" target="_blank" />,
							}}
							i18nKey="login.tos"
						/>
					</Typography>
				</Grid>
				<Grid item xs={12}>
					<Grid container justifyContent="space-between" alignItems="center" spacing={2}>
						<Grid item>
							<LogoWorldBankGroup width={180} />
						</Grid>
						<Grid item>
							<SelectLanguage />
						</Grid>
					</Grid>
				</Grid>
			</>
		);
	};

	// navigate to projects if authenticated
	useEffect(() => {
		if (paramError) {
			// Set status to DEFAULT when there's an error to ensure loading indicator is hidden
			setStatus('DEFAULT');

			const feedbackNew: Feedback = {
				alert: {
					children: t('error.auth'),
				},
			};
			if (paramError === 'deactivated') {
				feedbackNew.alert.children = t('error.userDeactivated');
			} else if (paramError === 'expired') {
				feedbackNew.alert.children = t('error.accessTokenExpired');
			} else if (paramError === 'unauthorized') {
				feedbackNew.alert.children = t('error.unauthorized');
			}
			setFeedback(feedbackNew);
		} else if (isAuthenticated && user) {
			const redirectUrl = sessionStorage.getItem('redirectUrl') || '/projects';
			navigate(redirectUrl);
		} else if (isAuthenticated && !user && inProgress === InteractionStatus.None) {
			// If authenticated but no user data yet, keep showing loading state
			setStatus('LOADING');
		}
	}, [inProgress, isAuthenticated, navigate, paramError, t, user]);

	// login immediately if specified in params
	useEffect(() => {
		if (paramIsLoginImmediate === 'true' && status === 'DEFAULT') {
			handleLogin();
		}
	}, [paramIsLoginImmediate, handleLogin, status]);

	return (
		<Gateway title={t('login.title')}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</Gateway>
	);
};
